import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Check, X } from "lucide-react";
import { apiError } from "@/lib/apiError";

export const PlanRequestsPanel = ({ onChange }) => {
  const { getAuthHeaders } = useAuth();
  const [requests, setRequests] = useState([]);
  const [plans, setPlans] = useState([]);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/seller-plans/requests?status=pending`, { headers: getAuthHeaders() });
      setRequests(res.data.requests || []);
      setPlans(res.data.plans || []);
    } catch (err) {
      toast.error(apiError(err, "Failed to load plan requests"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (id, action) => {
    try {
      await axios.put(`${API}/admin/seller-plans/requests/${id}`, { action }, { headers: getAuthHeaders() });
      toast.success(action === "approve" ? "Plan activated" : "Request rejected");
      load();
      onChange?.();
    } catch (err) {
      toast.error(apiError(err, "Failed to update request"));
    }
  };

  const planName = (id) => plans.find((p) => p.id === id)?.name || id;
  if (requests.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm mb-6 border-l-4 border-l-secondary-500" data-testid="plan-requests-panel">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg"><Crown className="w-5 h-5 text-secondary-500" />Pending Plan Requests ({requests.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {requests.map((r) => (
          <div key={r.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl bg-gray-50" data-testid={`plan-request-row-${r.id}`}>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{r.business_name} <span className="text-gray-500 text-sm">({r.mobile})</span></p>
              <p className="text-sm text-gray-600">{planName(r.current_plan)} → <strong>{planName(r.plan_id)}</strong></p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => act(r.id, "approve")} data-testid={`plan-request-approve-${r.id}`}>
                <Check className="w-4 h-4 mr-1" />Activate
              </Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => act(r.id, "reject")} data-testid={`plan-request-reject-${r.id}`}>
                <X className="w-4 h-4 mr-1" />Reject
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
